import { BasicOperatorType } from "@app/exports/enumerations";
import { LocationConfidenceScoreLabelEnum, ProviderNetworkType } from "@app/projects/enumerators";
import { DropdownItem, MultiselectDropdown, MultiselectNestedDropdown } from "@app/shared/checkbox-dropdown";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { FC, useContext, useMemo, useState } from "react";
import { ProviderHubContext } from "./contexts/ProviderHubContext";
import { NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { formatEnumValue } from "@app/shared/utility";
import WaffleCountBadge from "@app/shared/waffles/WaffleCountBadge";
import { ProviderHubFilterState, ProviderHubFilterValueType } from "./interfaces";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { isEqual } from "lodash";

const ProviderHubFilter: FC = () => {

  const [showFiltersForm, setShowFiltersForm] = useState<boolean>(false);
  const [providersFiltersCount, setProvidersFiltersCount] = useState<number>(0);
  const providerHubContext = useContext(ProviderHubContext);
  const projectContext = useContext(CountyProjectContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const numberFilterOperators: BasicOperatorType[] = [
    BasicOperatorType.Equal,
    BasicOperatorType.NotEqual,
    BasicOperatorType.Less,
    BasicOperatorType.LessEqual,
    BasicOperatorType.Greater,
    BasicOperatorType.GreaterEqual
  ];

  const stringFilterOperators: BasicOperatorType[] = [
    BasicOperatorType.Contains,
    BasicOperatorType.Equal,
  ];

  const networkTypeOptions: DropdownItem<string>[] = Object.values(ProviderNetworkType).filter(item => item != ProviderNetworkType.Unknown).map(item => ({
    key: item,
    name: formatEnumValue(item.toString()),
  }));

  const locationConfidenceOptions: DropdownItem<string>[] = Object.values(LocationConfidenceScoreLabelEnum).filter(item => item != LocationConfidenceScoreLabelEnum.Unknown).map(item => ({
    key: item,
    name: item.toString()
  }));

  const specialtiesImpacted: DropdownItem<string>[] = useMemo<DropdownItem<string>[]>(() => {
    // We only consider the specialties that the user has selected when loading the table data
    return providerHubContext.selectedSpecialties;

  }, [providerHubContext.selectedSpecialties]);

  const countiesImpacted: NestingParentDropdownItem[] = useMemo<NestingParentDropdownItem[]>(() => {

    const serviceAreasImpacted: NestingParentDropdownItem[] =  [];

    projectContext.runConfiguration.serviceArea.subRegions.map<void>((state) => {

      const martchingChildren: DropdownItem<string>[] = state.subRegions.filter(county => 
        // We only consider the service areas/counties that the user has selected when loading the table data
        providerHubContext.selectedServiceAreas.includes(county.regionCode)).map(county => {
         return { key: county.regionCode, name: county.displayName }
      });

      if (martchingChildren.length == 0) {
        return null;
      }

      const nestedServiceArea: NestingParentDropdownItem = {
        checkboxStatus: CheckboxStateEnum.Unchecked,
        children: martchingChildren,
        key: state.regionCode,
        name: state.displayName
      };

      serviceAreasImpacted.push(nestedServiceArea);

    });

    return serviceAreasImpacted;

  }, [providerHubContext.selectedServiceAreas]);

  const primaryContractEntities: DropdownItem<string>[] = useMemo<DropdownItem<string>[]>(() => {

    const uniqueContractEntities = Array.from(
      new Set(providerHubContext.providerHubServicingProviders.items.map(item => item.primaryContractEntity))
    );

    return uniqueContractEntities.map(value => ({
      key: value,
      name: value
    }));

  }, [providerHubContext.providerHubServicingProviders]);

  const getSelectionText = (currentSelections: unknown[], totalOptions: unknown[]) => {
    const current = currentSelections?.length;
    const total = totalOptions?.length;
    if (total === 0 || currentSelections == undefined) {
      return "Loading";
    }
    return `${current.toLocaleString()} of ${total.toLocaleString()} item(s) selected`;
  }

  const networkTypeSelectionText = useMemo(() => {
    return getSelectionText(providerHubContext.providersFilters['networkTypes']?.value as DropdownItem<string>[] ?? [], networkTypeOptions);
  }, [providerHubContext.providersFilters['newtorkTypes'], networkTypeOptions]);

  const specialtiesImpactedSelectionText = useMemo(() => {
    return getSelectionText(providerHubContext.providersFilters['specialtiesImpacted']?.value as DropdownItem<string>[] ?? [], specialtiesImpacted);
  }, [providerHubContext.providersFilters['specialtiesImpacted'], specialtiesImpacted]);

  const countiesImpactedSelectionText = useMemo(() => {
    return getSelectionText(providerHubContext.providersFilters['countiesImpacted']?.value as string[] ?? [], countiesImpacted.flatMap((c) => c.children));
  }, [providerHubContext.providersFilters['countiesImpacted'], countiesImpacted]);

  const primaryContractEntitiesSelectionText = useMemo(() => {
    return getSelectionText(providerHubContext.providersFilters['primaryContractEntity']?.value as DropdownItem<string>[] ?? [], primaryContractEntities);
  }, [providerHubContext.providersFilters['primaryContractEntity'], primaryContractEntities]);

  const locationConfidenceSelectionText = useMemo(() => {
    return getSelectionText(providerHubContext.providersFilters['locationConfidence']?.value as DropdownItem<string>[] ?? [], locationConfidenceOptions);
  }, [providerHubContext.providersFilters['locationConfidence'], locationConfidenceOptions]);

  const getValidFilters = (): ProviderHubFilterState => {

    return Object.entries(providerHubContext.providersFilters).reduce<ProviderHubFilterState>((accumulator, [key, filter]) => {

      const {operator, value} = filter;

      const hasOperator = !!operator;
      const hasValue = value != "" || (Array.isArray(value) && value.length > 0);

      const isValid = 
        (isValueOnlyFilter(key) && hasValue) ||  //Cases where the filter is value-only, e.g, there is only a dropdown in the value column
        (!isValueOnlyFilter(key) && hasOperator && hasValue) // Cases where both an operator and a value are expected and provided.

      if (isValid) {
        accumulator[key] = filter
      }

      return accumulator;

    }, {});
    
  };

  const isValueOnlyFilter = (columnKey: string): boolean => {
    switch (columnKey) {
      case "networkTypes":
      case "specialtiesImpacted": 
      case "countiesImpacted":
      case "primaryContractEntity":
      case "locationConfidence":
        return true;
      default:
        return false;
    }
  };

  const handleOperatorChange = (key: string, operator?: BasicOperatorType) => {
    providerHubContext.setProvidersFilters({
      ...providerHubContext.providersFilters,
      [key]: { ...providerHubContext.providersFilters[key], operator },
    });
  };

  const handleValueChange = (key: string, value: ProviderHubFilterValueType) => {
    providerHubContext.setProvidersFilters({
      ...providerHubContext.providersFilters,
      [key]: { ...providerHubContext.providersFilters[key], value },
    });
  };

  const handleApply = async () => {

    const validFilters = getValidFilters();
    if (!isEqual(providerHubContext.providersFilters, validFilters)){
      providerHubContext.setProvidersFilters(validFilters);
    }

    setProvidersFiltersCount(Object.keys(validFilters).length);

    providerHubContext.setTriggerProvidersRefreshData(true);
  };

  const handleClear = () => {
    providerHubContext.setProvidersFilters({});
    setProvidersFiltersCount(0);
  };

  return (
    <>
      <div className="mb-3 mt-2 provider-hub-filters">
        <QESButton
          qesType="secondary"
          qesTestId="provider-hub-filters__toggle-button"
          onClick={() => setShowFiltersForm(!showFiltersForm)}
          className="col-auto col-xl-auto provider-hub-filters__toggle-button"
        >
          {providersFiltersCount > 0 && 
            <div className="provider-hub-filter-count-badge">
              <WaffleCountBadge backgroundColor="#056edf" count={providersFiltersCount} textColor="white" />
            </div>  
          }
          Filters
        </QESButton>
        {showFiltersForm && (
          
          <div className="provider-hub-filters__container">

              <div className="provider-hub-filters__header">

                <div className="cell">Column</div>
                <div className="cell">Operator</div>
                <div className="cell">Value</div>
              </div>

              <div className="provider-hub-filters__row">

                <div className="cell">Name</div>
                <div className="cell">
                  <QESSelect
                    testId="provider-hub-filter__name-operator"
                    label=""
                    hideLabel={true}
                    allowEmpty={true}
                    required={false}
                    value={providerHubContext.providersFilters['providerName']?.operator ?? ''}
                    onChange={(e) => handleOperatorChange('providerName', e.target.value as BasicOperatorType)}
                  >
                    {stringFilterOperators.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </QESSelect>
                </div>
                <div className="cell">
                  <QESTextInput
                    label=""
                    type="text"
                    testId="provider-hub-filters__name-value"
                    value={providerHubContext.providersFilters['providerName']?.value as string ?? ''}
                    onChange={(e) => handleValueChange('providerName', e.target.value)}
                    />
                  </div>
              </div>

              <div className="provider-hub-filters__row">

                <div className="cell">Identifier</div>
                <div className="cell">
                  <QESSelect
                    testId="provider-hub-filters__identifier-operator"
                    label=""
                    hideLabel={true}
                    allowEmpty={true}
                    required={false}
                    value={providerHubContext.providersFilters['npi']?.operator ?? ''}
                    onChange={(e) => handleOperatorChange('npi', e.target.value as BasicOperatorType)}
                  >
                    {stringFilterOperators.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </QESSelect>
                </div>
                <div className="cell">
                  <QESTextInput
                    label=""
                    type="text"
                    testId="provider-hub-filters__identifier-value"
                    value={providerHubContext.providersFilters['npi']?.value as string ?? ''}
                    onChange={(e) => handleValueChange('npi', e.target.value)}
                  />
                </div>

              </div>

              <div className="provider-hub-filters__row">

                <div className="cell">Network Type</div>
                <div className="cell"></div>
                <div className="cell">
                  <MultiselectDropdown
                    customWidth={true}
                    customSelectInputText={networkTypeSelectionText}
                    hideLabel={true}
                    items={networkTypeOptions}
                    label=""
                    onSelectionChanged={(selectedItems) => handleValueChange('networkTypes', selectedItems)}
                    selectedItems={providerHubContext.providersFilters['networkTypes']?.value as DropdownItem<string>[] ?? []}
                    testId="provider-hub-filters__network-types-value"
                    showTooltipOnOverflow={true}
                  />
                </div>
              </div>

              <div className="provider-hub-filters__row">

                <div className="cell">Gap impact</div>
                <div className="cell">
                  <QESSelect
                    testId="provider-hub-filters__gap-impact-operator"
                    label=""
                    hideLabel={true}
                    allowEmpty={true}
                    required={false}
                    value={providerHubContext.providersFilters['gapsClosed']?.operator ?? ''}
                    onChange={(e) => handleOperatorChange('gapsClosed', e.target.value as BasicOperatorType)}
                  >
                    {numberFilterOperators.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </QESSelect>
                </div>
                <div className="cell">
                  <QESTextInput
                    label=""
                    onlyAllowNumbers={true}
                    testId="provider-hub-filters__gap-impact-value"
                    min={0}
                    step={1}
                    value={providerHubContext.providersFilters['gapsClosed']?.value as string ?? ''}
                    onChange={(e) => handleValueChange('gapsClosed', e.target.value as BasicOperatorType)}
                    //Prevents the user from typing a negative value or using scientific notation
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e') {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>

              <div className="provider-hub-filters__row">

                <div className="cell">Specialties Impacted</div>
                <div className="cell"></div>
                <div className="cell">
                  <MultiselectDropdown
                    customWidth={true}
                    customSelectInputText={specialtiesImpactedSelectionText}
                    hideLabel={true}
                    items={specialtiesImpacted}
                    label=""
                    onSelectionChanged={(selectedItems) => handleValueChange('specialtiesImpacted', selectedItems)}
                    selectedItems={providerHubContext.providersFilters['specialtiesImpacted']?.value as DropdownItem<string>[] ?? []}
                    testId="provider-hub-filters__specialties-impacted-value"
                    showTooltipOnOverflow={true}
                  />
                </div>

              </div>

              <div className="provider-hub-filters__row">
                
                <div className="cell">Counties Impacted</div>
                <div className="cell"></div>
                <div className="cell">
                  <MultiselectNestedDropdown
                    customSelectInputText={countiesImpactedSelectionText}
                    items={countiesImpacted}
                    label=""
                    onSelectionChanged={(selectedItems) => handleValueChange('countiesImpacted', selectedItems)}
                    selectedItems={providerHubContext.providersFilters['countiesImpacted']?.value as string[] ?? []}
                    testId="provider-hub-filters__counties-impacted-value"
                  />
                </div>
              </div>

              <div className="provider-hub-filters__row">

                <div className="cell">Access Points</div>
                <div className="cell">
                  <QESSelect
                    testId="provider-hub-filters__access-points-operator"
                    label=""
                    hideLabel={true}
                    allowEmpty={true}
                    required={false}
                    value={providerHubContext.providersFilters['accessPointCount']?.operator ?? ''}
                    onChange={(e) => handleOperatorChange('accessPointCount', e.target.value as BasicOperatorType)}
                  >
                    {numberFilterOperators.map((op) => (
                      <option key={op} value={op}>
                        {op}
                      </option>
                    ))}
                  </QESSelect>
                </div>
                <div className="cell">
                  <QESTextInput
                    label=""
                    type="number"
                    testId="provider-hub-filters__access-points-value"
                    onlyAllowNumbers={true}
                    min={0}
                    step={1}
                    value={providerHubContext.providersFilters['accessPointCount']?.value as string ?? ''}
                    onChange={(e) => handleValueChange('accessPointCount', e.target.value as BasicOperatorType)}
                    //Prevents the user from typing a negative value or using scientific notation
                    onKeyDown={(e) => {
                      if (e.key === '-' || e.key === 'e') {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>

              </div>

              {featureFlags.has(Features.PROVIDER_HUB_CONTRACT_ENTITY) && 

                <div className="provider-hub-filters__row">

                  <div className="cell">Primary Contract Entity</div>
                  <div className="cell"></div>
                  <div className="cell">
                    <MultiselectDropdown
                      customWidth={true}
                      customSelectInputText={primaryContractEntitiesSelectionText}
                      hideLabel={true}
                      items={primaryContractEntities}
                      label=""
                      onSelectionChanged={(selectedItems) => handleValueChange('primaryContractEntity', selectedItems)}
                      selectedItems={providerHubContext.providersFilters['primaryContractEntity']?.value as DropdownItem<string>[] ?? []}
                      testId="provider-hub-filters__primary-contract-entity-value"
                      showTooltipOnOverflow={true}
                    />
                  </div>

                </div>
              }        
              
              {projectContext.project.showLocationConfidenceScores && projectContext.runConfiguration?.showLocationConfidenceScores === true && 

                <div className="provider-hub-filters__row">

                  <div className="cell">Location Confidence Score</div>
                  <div className="cell"></div>
                  <div className="cell">
                    <MultiselectDropdown
                      customWidth={true}
                      customSelectInputText={locationConfidenceSelectionText}
                      hideLabel={true}
                      items={locationConfidenceOptions}
                      label=""
                      onSelectionChanged={(selectedItems) => handleValueChange('locationConfidence', selectedItems)}
                      selectedItems={providerHubContext.providersFilters['locationConfidence']?.value as DropdownItem<string>[] ?? []}
                      testId="provider-hub-filters__location-confidence-value"
                      showTooltipOnOverflow={true}
                    />
                  </div>

                </div>
              }

              <div className="d-flex col-12 col-xl provider-hub-filter-buttons">
                <>
                  <QESButton
                    className="col-auto col-xl-auto provider-hub-filters__clear-button"
                    onClick={handleClear}
                    qesType="secondary"
                    qesTestId="provider-hub-filters__clear-button">
                    Clear All
                  </QESButton>

                  <QESButton
                    disabled={false}
                    className="col-auto col-xl-auto"
                    onClick={handleApply}
                    qesType="primary"
                    qesTestId="provider-hub-filters__apply-button">
                    Apply
                  </QESButton>
                </>

            </div>
          </div>

        )}

      </div>
    </>
  );

}

export default ProviderHubFilter;